import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from 'react';
import type { SuggestedAction } from '../../types';

// One block's registered suggested-action row.
export interface FloatingActionEntry {
  node: HTMLElement | null;
  actions: SuggestedAction[];
  onSelect: (promptTemplate: string) => void;
  disabled: boolean;
  testIdPrefix: string;
}

export interface FloatingActionsContextValue {
  entries: Map<string, FloatingActionEntry>;
  register: (id: string, entry: FloatingActionEntry) => void;
  unregister: (id: string) => void;
}

export const FloatingActionsContext =
  createContext<FloatingActionsContextValue | null>(null);

// Identity of the label-bearing bits, so the registry updates on chip changes
// (e.g. a selection count) but not on unrelated re-renders.
const actionsKey = (actions: SuggestedAction[]): string =>
  actions.map((a) => `${a.id}:${a.kind}:${a.label}`).join('|');

// Register a block's suggested actions; returns the ref for its chip row.
// No-ops without a provider (so blocks render fine in tests).
export const useRegisterSuggestedActions = (entry: {
  actions: SuggestedAction[];
  onSelect: (promptTemplate: string) => void;
  disabled: boolean;
  testIdPrefix: string;
  // The floating copy sets this false so it doesn't register itself and loop.
  enabled: boolean;
}): ((element: HTMLDivElement | null) => void) => {
  const id = useId();
  // A callback ref (not a RefObject): React can swap the node on re-render, and
  // a captured `ref.current` would leave a detached node in the registry.
  const [node, setNode] = useState<HTMLDivElement | null>(null);
  const setNodeRef = useCallback(
    (element: HTMLDivElement | null) => setNode(element),
    []
  );
  const ctx = useContext(FloatingActionsContext);
  // Depend on the stable callbacks, not the context object — registering swaps
  // the context value, which would re-fire the effect and loop forever.
  const register = ctx?.register;
  const unregister = ctx?.unregister;
  // Latest onSelect without re-registering (the block hands us a fresh one).
  const onSelectRef = useRef(entry.onSelect);
  onSelectRef.current = entry.onSelect;

  const { actions, disabled, testIdPrefix, enabled } = entry;
  const key = actionsKey(actions);
  useEffect(() => {
    if (!register || !unregister) return;
    if (!enabled || actions.length === 0 || !node) {
      unregister(id);
      return;
    }
    register(id, {
      node,
      actions,
      disabled,
      testIdPrefix,
      onSelect: (t) => onSelectRef.current(t),
    });
    return () => unregister(id);
    // `key` stands in for `actions` — it encodes the id/kind/label of each chip.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [register, unregister, id, key, enabled, disabled, testIdPrefix, node]);

  return setNodeRef;
};

// All registered rows with a mounted node, sorted by document order (last =
// the most recent output). Empty rows are ignored.
export const useSuggestedActionsEntries = (): FloatingActionEntry[] => {
  const ctx = useContext(FloatingActionsContext);
  return useMemo(() => {
    if (!ctx) return [];
    return (
      [...ctx.entries.values()]
        // Drop detached nodes — they'd scramble the document-order sort.
        .filter((e) => !!e.node && e.node.isConnected !== false)
        .sort((a, b) =>
          a.node!.compareDocumentPosition(b.node!) &
          Node.DOCUMENT_POSITION_FOLLOWING
            ? -1
            : 1
        )
    );
  }, [ctx]);
};
